import { type MarketplaceAmazon, marketplaceToConfig } from '@amzn/journeys-common';
import { DestinationUrlInput } from 'features/campaign/campaign.types';
import { LINK_CONFIG, URL_PATTERNS } from './constants';
import { sanitizeRefTag } from './refTagsUtils';

/**
 * Parses a URL to extract link type, value, ref tag, and show variations flag
 *
 * @param url - The URL to parse
 * @param platform - platform of the flavor (DESKTOP or MOBILE)
 * @returns An object containing:
 *   - linkType: The type of link (asin, browse, deals, manual)
 *   - linkValue: The value of the links
 *   - refTag: The reference tag (if any)
 *   - showVariations: Whether to show variations (only for deals)
 */
export const parseUrl = (
  url: string,
  platform?: string,
): {
  linkType: DestinationUrlInput;
  linkValue: string;
  refTag: string;
  showVariations?: boolean;
} => {
  if (!url) {
    return {
      linkType: DestinationUrlInput.ASIN,
      linkValue: '',
      refTag: '',
    };
  }

  const refMatch = url.match(/ref_?=([^&/]+)/);
  const refTag = refMatch?.[1] ?? '';

  for (const [linkType, pattern] of Object.entries(URL_PATTERNS)) {
    const match = url.match(pattern);
    if (match?.[1]) {
      if (refTag) {
        const hasValidRefFormat =
          (linkType === DestinationUrlInput.ASIN && url.includes(`/ref=${refTag}`)) ||
          (linkType === DestinationUrlInput.BROWSE_NODE && url.includes(`&ref_=${refTag}`)) ||
          (linkType === DestinationUrlInput.DEALS && url.includes(`&ref=${refTag}`));

        if (!hasValidRefFormat) {
          continue;
        }
      }

      let linkValue = match[1];

      if (linkType === DestinationUrlInput.ASIN && platform) {
        const isDesktopUrl = url.includes('/dp/');
        const isMobileUrl = url.includes('/gp/aw/d/');
        const isMobilePlatform = platform === 'MOBILE';

        if ((isDesktopUrl && isMobilePlatform) || (isMobileUrl && !isMobilePlatform)) {
          continue;
        }
      }

      if (linkType === DestinationUrlInput.DEALS) {
        const moreDealsMatch = url.match(/[?&]moreDeals=([^&]+)/);
        if (moreDealsMatch) {
          linkValue = `${match[1]},${moreDealsMatch[1]}`;
        }
      }

      if (linkType === DestinationUrlInput.LINK) {
        linkValue = url.replace(/ref_?=[^&/]+/g, 'ref=');
      }

      const result = {
        linkType,
        linkValue,
        refTag,
      } as {
        linkType: DestinationUrlInput;
        linkValue: string;
        refTag: string;
        showVariations?: boolean;
      };

      if (linkType === DestinationUrlInput.DEALS) {
        if (url.includes('showVariations=true')) {
          result.showVariations = true;
        } else if (url.includes('showVariations=false')) {
          result.showVariations = false;
        }
      }

      return result;
    }
  }

  return {
    linkType: DestinationUrlInput.LINK,
    linkValue: url.replace(/ref_?=[^&/]+/g, 'ref='),
    refTag,
  };
};

/**
 * Generates ASIN URL based on platform (desktop or mobile)
 *
 * @param linkValue - The ASIN value
 * @param platform - The platform of the flavor (DESKTOP or MOBILE)
 * @returns The ASIN URL path
 * @example
 * generateAsinUrl('B123456789', 'DESKTOP') // '/dp/B123456789'
 * generateAsinUrl('B123456789', 'MOBILE')  // '/gp/aw/d/B123456789'
 */
const generateAsinUrl = (linkValue: string, platform?: string): string => {
  const isMobile = platform === 'MOBILE';
  const urlBaseAsin = isMobile ? '/gp/aw/d/' : '/dp/';
  return `${urlBaseAsin}${linkValue}`;
};

/**
 * Generates Browse Node URL
 *
 * @param linkValue - The browse node value
 * @returns The browse node URL path
 * @example
 * generateBrowseNodeUrl('6669703011') // '/gp/browse.html?node=6669703011'
 */
const generateBrowseNodeUrl = (linkValue: string): string => `/gp/browse.html?node=${linkValue}`;

/**
 * Generates Deals URL with optional variations and support for multiple deal IDs
 *
 * @param linkValue - Comma-separated deal IDs
 * @param showVariations - Whether to show variations (defaults to true)
 * @returns The deals URL path with query parameters, or empty string if no valid deals
 * @example
 * generateDealsUrl('abc123', true)           // '/deal/abc123?showVariations=true'
 * generateDealsUrl('abc,def', false)         // '/deal/abc?showVariations=false&moreDeals=def'
 * generateDealsUrl('abc, def, abc')          // '/deal/abc?showVariations=true&moreDeals=def'
 */
const generateDealsUrl = (linkValue: string, showVariations?: boolean): string => {
  const filteredInputDeals = linkValue
    .toLowerCase()
    .replace(/,+/g, ',')
    .replace(/,$/, '')
    .replace(/\s+/g, '')
    .split(',')
    .filter((dealId) => dealId.length > 0);

  if (filteredInputDeals.length === 0) {
    return '';
  }

  const uniqueDealIds = [
    ...new Set(filteredInputDeals),
  ];
  const [firstDeal, ...remainingDeals] = uniqueDealIds;

  const params = [
    `showVariations=${showVariations ?? true}`,
    remainingDeals.length > 0 && `moreDeals=${remainingDeals.join(',')}`,
  ]
    .filter(Boolean)
    .join('&');

  return `/deal/${firstDeal}?${params}`;
};

/**
 * Generates manual link URL (returns as-is)
 *
 * @param linkValue - The manual URL path
 * @returns The manual URL path unchanged
 * @example
 * generateManualUrl('/custom/path') // '/custom/path'
 */
const generateManualUrl = (linkValue: string): string => linkValue;

/**
 * Generates a URL without refTag based on the selected link type, value, and show variations flag
 * Used by LinkUrlBuilder which manages URL field separately from refTag field
 *
 * @param linkType - The type of link (asin, browse, deals, manual)
 * @param linkValue - The value of the link
 * @param platform - The platform of the flavor
 * @param showVariations - Whether to show variations (only for deals)
 * @returns The URL without refTag or empty string if required parameters are missing
 */
export const generateUrlWithoutRefTag = (
  linkType: DestinationUrlInput,
  linkValue: string,
  platform?: string,
  showVariations?: boolean,
): string => {
  if (!linkType || !linkValue) return '';

  const config = LINK_CONFIG[linkType];
  if (!config) return '';

  if (linkType === DestinationUrlInput.BROWSE_NODE) {
    return generateBrowseNodeUrl(linkValue);
  }

  if (linkType === DestinationUrlInput.ASIN) {
    return generateAsinUrl(linkValue, platform);
  }

  if (linkType === DestinationUrlInput.DEALS) {
    return generateDealsUrl(linkValue, showVariations);
  }

  // for manual links
  return generateManualUrl(linkValue);
};

/**
 * Appends refTag to a URL based on the link type
 *
 * @param url - The base URL without refTag
 * @param linkType - The type of link (asin, browse, deals, manual)
 * @param refTag - The reference tag to append
 * @returns The URL with refTag appended
 */
export const appendRefTagToUrl = (url: string, linkType: DestinationUrlInput, refTag: string): string => {
  if (!refTag || !url) return url;

  const sanitizedRefTag = sanitizeRefTag(refTag);
  if (!sanitizedRefTag) return url;

  if (linkType === DestinationUrlInput.BROWSE_NODE) {
    return `${url}&ref_=${sanitizedRefTag}`;
  }

  if (linkType === DestinationUrlInput.ASIN) {
    return `${url}/ref=${sanitizedRefTag}`;
  }

  if (linkType === DestinationUrlInput.DEALS) {
    return `${url}&ref=${sanitizedRefTag}`;
  }

  // for manual links
  return `${url}${sanitizedRefTag}`;
};

/**
 * Generates a preview URL based on the selected link type, value, ref tag, and show variations flag
 *
 * @param linkType - The type of link (asin, browse, deals, manual)
 * @param linkValue - The value of the link
 * @param refTag - The reference tag
 * @param platform - The platform of the flavor
 * @param showVariations - Whether to show variations (only for deals)
 * @returns The complete preview URL or empty string if required parameters are missing
 */
export const generatePreviewUrl = (
  linkType: DestinationUrlInput,
  linkValue: string,
  refTag: string,
  platform?: string,
  showVariations?: boolean,
): string => {
  const baseUrl = generateUrlWithoutRefTag(linkType, linkValue, platform, showVariations);
  return appendRefTagToUrl(baseUrl, linkType, refTag);
};

/**
 * Generates a complete destination URL with the correct marketplace amazon domain
 *
 * @param marketplace - The marketplace code (e.g., 'US', 'DE', 'GB')
 * @param urlPath - The relative URL path (e.g., '/dp/B012345678')
 * @returns The complete URL with https and marketplace domain (e.g., 'https://amazon.com/dp/B012345678')
 */
export const generateDestinationUrl = (marketplace: string, urlPath: string): string => {
  const currentMarketplace = `${marketplace}Amazon` as MarketplaceAmazon;
  const marketplaceDomain = marketplaceToConfig[currentMarketplace].siteName;
  return `https://${marketplaceDomain}${urlPath}`;
};
